import React, { useState } from "react";
import payrollService from "../../services/payrollService";
import Notification from "../../components/UI/Notification";
import { FileDown, Database, FileText, DollarSign } from "lucide-react";

const ExportHub = () => {
  const [activeTab, setActiveTab] = useState("bank");
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [bankForm, setBankForm] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    format: "csv",
    employee_ids: [],
  });
  const [statutoryForm, setStatutoryForm] = useState({
    report_type: "pf",
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    format: "xlsx",
  });

  const handleBankExport = async () => {
    setLoading(true);
    try {
      // Convert month to integer as required by API
      const payload = {
        ...bankForm,
        month: parseInt(bankForm.month, 10),
        year: parseInt(bankForm.year, 10),
      };
      
      const blob = await payrollService.generateBankFile(payload);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `bank_transfer_${bankForm.year}_${bankForm.month}.${bankForm.format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setNotification({
        type: "success",
        message: "Bank file exported successfully",
      });
    } catch (error) {
      console.error("Bank export error:", error);
      setNotification({
        type: "error",
        message: "Failed to export bank file",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatutoryExport = async () => {
    setLoading(true);
    try {
      // Convert month and year to integers as required by API
      const payload = {
        ...statutoryForm,
        month: parseInt(statutoryForm.month, 10),
        year: parseInt(statutoryForm.year, 10),
      };
      
      const blob = await payrollService.generateStatutoryReport(payload);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `statutory_${statutoryForm.report_type}_${statutoryForm.year}_${statutoryForm.month}.${statutoryForm.format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setNotification({
        type: "success",
        message: "Statutory report exported successfully",
      });
    } catch (error) {
      console.error("Statutory export error:", error);
      setNotification({
        type: "error",
        message: "Failed to export statutory report",
      });
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: "bank", label: "Bank Transfer", icon: <DollarSign size={18} /> },
    { id: "statutory", label: "Statutory Reports", icon: <FileText size={18} /> },
    { id: "accounting", label: "Accounting Data", icon: <Database size={18} /> },
    { id: "payroll", label: "Payroll Data", icon: <FileDown size={18} /> },
  ];

  return (
    <div className="p-6 font-poppins">
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Export Hub
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Export payroll data in various formats
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors ${
              activeTab === tab.id
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Bank Transfer Tab */}
      {activeTab === "bank" && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Generate Bank Transfer File
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Year
              </label>
              <input
                type="number"
                value={bankForm.year}
                onChange={(e) => setBankForm({ ...bankForm, year: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Month
              </label>
              <input
                type="number"
                min="1"
                max="12"
                value={bankForm.month}
                onChange={(e) => setBankForm({ ...bankForm, month: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Format
              </label>
              <select
                value={bankForm.format}
                onChange={(e) => setBankForm({ ...bankForm, format: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="csv">CSV</option>
                <option value="txt">TXT</option>
              </select>
            </div>
          </div>
          <button
            onClick={handleBankExport}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium disabled:opacity-50"
          >
            {loading ? "Exporting..." : "Export Bank File"}
          </button>
        </div>
      )}

      {/* Statutory Reports Tab */}
      {activeTab === "statutory" && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Generate Statutory Report
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Report Type
              </label>
              <select
                value={statutoryForm.report_type}
                onChange={(e) => setStatutoryForm({ ...statutoryForm, report_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="pf">Provident Fund (PF)</option>
                <option value="esi">ESI</option>
                <option value="pt">Professional Tax (PT)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Year
              </label>
              <input
                type="number"
                value={statutoryForm.year}
                onChange={(e) => setStatutoryForm({ ...statutoryForm, year: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Month
              </label>
              <input
                type="number"
                min="1"
                max="12"
                value={statutoryForm.month}
                onChange={(e) => setStatutoryForm({ ...statutoryForm, month: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Format
              </label>
              <select
                value={statutoryForm.format}
                onChange={(e) => setStatutoryForm({ ...statutoryForm, format: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="xlsx">Excel (XLSX)</option>
                <option value="pdf">PDF</option>
              </select>
            </div>
          </div>
          <button
            onClick={handleStatutoryExport}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium disabled:opacity-50"
          >
            {loading ? "Exporting..." : "Export Statutory Report"}
          </button>
        </div>
      )}

      {/* Other tabs can be similarly implemented */}
      {(activeTab === "accounting" || activeTab === "payroll") && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <p className="text-center text-gray-600 dark:text-gray-400">
            {activeTab === "accounting" ? "Accounting" : "Payroll"} data export coming soon!
          </p>
        </div>
      )}
    </div>
  );
};

export default ExportHub;

